import DOMPurify from 'dompurify';

export const ALLOWED_TAGS = new Set([
  'p',
  'div',
  'span',
  'br',
  'hr',
  'blockquote',
  'pre',
  'figure',
  'figcaption',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'mark',
  'sub',
  'sup',
  'small',
  'code',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'caption',
  'colgroup',
  'col',
  'a',
  'img',
  'iframe',
  'oembed',
  'audio',
  'video',
  'source',
]);

export const ALLOWED_ATTRS = new Set([
  'id',
  'class',
  'style',
  'title',
  'dir',
  'lang',
  'href',
  'target',
  'rel',
  'src',
  'url',
  'alt',
  'width',
  'height',
  'allowfullscreen',
  'frameborder',
  'webkitallowfullscreen',
  'mozallowfullscreen',
  'controls',
  'preload',
  'poster',
  'colspan',
  'rowspan',
  'scope',
  'data-name',
]);

export const URI_ATTRS = new Set(['href', 'src', 'poster']);

const ID_PATTERN = /^[a-zA-Z][\w-]*$/;

export const isSafeId = (value: string): boolean => ID_PATTERN.test(value);

const ALLOWED_URI_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);
const SCHEME_PATTERN = /^([a-z][a-z0-9+.\-]*):/i;

export const isSafeUri = (value: string): boolean => {
  const cleaned = value.replace(/[\s\x00-\x1F\x7F]/g, '');
  if (cleaned.startsWith('//')) return false;
  const match = cleaned.match(SCHEME_PATTERN);
  if (!match) return true;
  return ALLOWED_URI_SCHEMES.has(match[1].toLowerCase());
};

const HTML_PREVIEW_PURIFY_TAGS = [
  ...Array.from(ALLOWED_TAGS),
  'input',
  'label',
];

const HTML_PREVIEW_PURIFY_ATTRS = [
  ...Array.from(ALLOWED_ATTRS),
  'type',
  'disabled',
  'checked',
  'name',
];

const HTML_PREVIEW_PURIFY_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: HTML_PREVIEW_PURIFY_TAGS,
  ALLOWED_ATTR: HTML_PREVIEW_PURIFY_ATTRS,
  ADD_ATTR: ['data-name'],
  FORBID_ATTR: ['srcdoc', 'hidden'],
  ALLOW_DATA_ATTR: false,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false,
};

const NEG_NUMBER_PATTERN = /^-\s*\d/;
const SCALE_ZERO_PATTERN = /scale\s*\(\s*0+(?:\.0+)?\s*[,)]/i;
const RECT_ZERO_PATTERN =
  /rect\s*\(\s*0(?:px)?\s*[, ]\s*0(?:px)?\s*[, ]\s*0(?:px)?\s*[, ]\s*0(?:px)?\s*\)/i;
const POSITION_FORBIDDEN = /\b(fixed|sticky)\b/i;
const OFFSCREEN_PROPS = new Set(['top', 'left', 'right', 'bottom']);
const Z_INDEX_THRESHOLD = 1000;

type StyleRule = (prop: string, val: string) => boolean;

const FORBIDDEN_STYLE_RULES: StyleRule[] = [
  (_prop, val) => val.toLowerCase().includes('url('),
  (prop, val) => prop === 'position' && POSITION_FORBIDDEN.test(val),
  (prop, val) => {
    if (prop !== 'opacity') return false;
    const n = parseFloat(val.replace(/\s/g, ''));
    return !Number.isNaN(n) && n === 0;
  },
  (prop, val) => prop === 'visibility' && val.toLowerCase() === 'hidden',
  (prop, val) => prop === 'clip' && RECT_ZERO_PATTERN.test(val),
  (prop, val) => prop === 'transform' && SCALE_ZERO_PATTERN.test(val),
  (prop, val) => prop === 'pointer-events' && val.toLowerCase() === 'none',
  (prop, val) => OFFSCREEN_PROPS.has(prop) && NEG_NUMBER_PATTERN.test(val),
  (prop, val) => {
    if (prop !== 'z-index') return false;
    const n = parseInt(val, 10);
    return !Number.isNaN(n) && Math.abs(n) >= Z_INDEX_THRESHOLD;
  },
];

const isForbiddenStyleDecl = (prop: string, val: string): boolean =>
  FORBIDDEN_STYLE_RULES.some(rule => rule(prop, val));

export function sanitizePreviewInlineStyle(style: string): string {
  const parts: string[] = [];
  for (const segment of style.split(';')) {
    const idx = segment.indexOf(':');
    if (idx === -1) continue;
    const prop = segment.slice(0, idx).trim().toLowerCase();
    const val = segment.slice(idx + 1).trim();
    if (!prop || !val) continue;
    if (isForbiddenStyleDecl(prop, val)) continue;

    parts.push(`${prop}: ${val}`);
  }
  return parts.join('; ');
}

function applyPreviewInlineStylePass(html: string): string {
  if (typeof document === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll('[style]').forEach(el => {
    const raw = el.getAttribute('style');
    if (raw == null) return;
    const cleaned = sanitizePreviewInlineStyle(raw);
    if (cleaned) el.setAttribute('style', cleaned);
    else el.removeAttribute('style');
  });
  return doc.body.innerHTML;
}

export function sanitizeHtmlForHtmlPreview(html: string): string {
  const out = DOMPurify.sanitize(html, HTML_PREVIEW_PURIFY_CONFIG);
  const str = typeof out === 'string' ? out : '';
  return applyPreviewInlineStylePass(str);
}
